const DRIVER_DOB_FALLBACK = "1900-01-01";

export const toNullableInt = (value) => {
  if (value === "" || value === undefined || value === null) {
    return null;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

export const toRequiredInt = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

export const toNullableDate = (value) => {
  const trimmedValue = value === undefined || value === null
    ? ""
    : String(value).trim();

  if (!trimmedValue) {
    return null;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
    return trimmedValue;
  }

  const parsedDate = new Date(trimmedValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate.toISOString().split("T")[0];
};

const toTrimmedString = (value) => {
  if (value === undefined || value === null) {
    return "";
  }

  return String(value).trim();
};

const toNullableString = (value) => {
  const trimmedValue = toTrimmedString(value);
  return trimmedValue === "" ? null : trimmedValue;
};

export const normalizeDriverDob = (value) => {
  const trimmedValue = toTrimmedString(value);

  if (!trimmedValue) {
    return DRIVER_DOB_FALLBACK;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
    return trimmedValue;
  }

  const parsedDate = new Date(trimmedValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return DRIVER_DOB_FALLBACK;
  }

  return parsedDate.toISOString().split("T")[0];
};

export const buildCreateDriverPayload = (formData) => ({
  driver_id: toTrimmedString(formData.driverId),
  first_name: toTrimmedString(formData.firstName),
  last_name: toTrimmedString(formData.lastName),
  email: toNullableString(formData.email),
  vehicle_id: toNullableInt(formData.vehicle_id ?? formData.assignedVehicle),
  phone_number: toTrimmedString(formData.phone_number ?? formData.phoneNumber),
  driving_license_number: toTrimmedString(
    formData.dlNumber ??
      formData.drivingLicenseId ??
      formData.driving_license_number,
  ),
  dob: normalizeDriverDob(formData.dob ?? formData.dateOfBirth),
  licenseExpiryDate: toNullableDate(formData.licenseExpiryDate),
  supervisorId: toNullableInt(formData.supervisorId),
  current_address: toTrimmedString(
    formData.presentAddress ?? formData.current_address,
  ),
  current_city: toTrimmedString(formData.presentCity ?? formData.current_city),
  current_pincode: toTrimmedString(
    formData.presentPincode ?? formData.current_pincode,
  ),
  permanent_address: toTrimmedString(
    formData.permanentAddress ?? formData.permanent_address,
  ),
  permanent_city: toTrimmedString(
    formData.permanentCity ?? formData.permanent_city,
  ),
  permanent_pincode: toTrimmedString(
    formData.permanentPincode ?? formData.permanent_pincode,
  ),
  description: toTrimmedString(formData.description ?? formData.moreDetails),
  organizationId: toRequiredInt(formData.organizationId),
});

export const buildUpdateDriverPayload = (formData) => ({
  driverId: toTrimmedString(formData.driverId),
  firstName: toTrimmedString(formData.firstName),
  lastName: toTrimmedString(formData.lastName),
  email: toNullableString(formData.email),
  vehicle_id: toNullableInt(formData.vehicle_id ?? formData.assignedVehicle),
  phone_number: toTrimmedString(formData.phone_number ?? formData.phoneNumber),
  driving_license_number: toTrimmedString(
    formData.dlNumber ??
      formData.drivingLicenseId ??
      formData.driving_license_number,
  ),
  dob: normalizeDriverDob(formData.dob ?? formData.dateOfBirth),
  licenseExpiryDate: toNullableDate(formData.licenseExpiryDate),
  supervisorId: toNullableInt(formData.supervisorId),
  current_address: toTrimmedString(
    formData.presentAddress ?? formData.current_address,
  ),
  current_city: toTrimmedString(formData.presentCity ?? formData.current_city),
  current_pincode: toTrimmedString(
    formData.presentPincode ?? formData.current_pincode,
  ),
  permanent_address: toTrimmedString(
    formData.permanentAddress ?? formData.permanent_address,
  ),
  permanent_city: toTrimmedString(
    formData.permanentCity ?? formData.permanent_city,
  ),
  permanent_pincode: toTrimmedString(
    formData.permanentPincode ?? formData.permanent_pincode,
  ),
  description: toTrimmedString(formData.description ?? formData.moreDetails),
});

export const buildAssignDriverVehiclePayload = (driverId, vehicleId) => ({
  driverId: toRequiredInt(driverId),
  vehicleId: toRequiredInt(vehicleId),
});

export const mapDriverApiToFormData = (driverData, fallbackOrganizationId) => {
  const firstName = driverData.first_name || driverData.firstName || "";
  const lastName = driverData.last_name || driverData.lastName || "";
  const dob = driverData.dob ? driverData.dob.split("T")[0] : "";
  const licenseExpiryDate = driverData.licenseExpiryDate
    ? String(driverData.licenseExpiryDate).split("T")[0]
    : "";
  const currentAddress = driverData.current_address || "";
  const currentCity = driverData.current_city || "";
  const currentPincode = driverData.current_pincode || "";
  const permanentAddress = driverData.permanent_address || "";
  const permanentCity = driverData.permanent_city || "";
  const permanentPincode = driverData.permanent_pincode || "";
  const vehicleId =
    driverData.vehicle?.id ?? toNullableInt(driverData.vehicle_id) ?? "";

  return {
    driverId: driverData.driver_id || driverData.driverId || "",
    organizationId:
      driverData.organizationId ||
      driverData.organization_id ||
      fallbackOrganizationId,
    firstName,
    lastName,
    email: driverData.email || "",
    phone_number: driverData.phone_number || "",
    driving_license_number: driverData.driving_license_number || "",
    dob,
    current_address: currentAddress,
    current_city: currentCity,
    current_pincode: currentPincode,
    permanent_address: permanentAddress,
    permanent_city: permanentCity,
    permanent_pincode: permanentPincode,
    description: driverData.description || "",
    fullName: `${firstName} ${lastName}`.trim(),
    sameAsPresentAddress:
      Boolean(currentAddress) &&
      Boolean(permanentAddress) &&
      currentAddress === permanentAddress &&
      currentCity === permanentCity &&
      currentPincode === permanentPincode,
    isActive: true,
    hasSmartphone: false,
    preferredLanguage: "",
    presentAddress: currentAddress,
    presentCity: currentCity,
    presentPincode: currentPincode,
    permanentAddress,
    permanentCity,
    permanentPincode,
    phoneNumber: driverData.phone_number || "",
    drivingLicenseId: driverData.driving_license_number || "",
    dateOfBirth: dob,
    licenseExpiryDate,
    moreDetails: driverData.description || "",
    vehicle_id: vehicleId,
    supervisorId: driverData.supervisorId ?? driverData.supervisor?.id ?? "",
    _showVehicleSelect: false,
  };
};
